import { useCallback, useEffect, useMemo, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { Box, Button, IconButton } from '@mui/material';

import { defaultFieldValue, hasDurableValue } from '../../fieldPersistence';
import {
  addRepeatRecord,
  duplicateRepeatRecordError,
  type IntakeRepeatRecord,
  pendingSuggestionRecordIds,
  removeRepeatRecord,
  repeatDuplicatePolicy,
  repeatRecordIdForSuggestion,
  suggestionEntryKey,
  updateRepeatRecord,
} from '../../repeatableFields';
import type { GeneratedIntakeField, IntakeFieldSuggestion } from '../../types';

import { type IntakeFieldSaveState, IntakePageFieldInput } from './IntakePageFieldInput';
import { cardSx, wf } from './tokens';

const PRACTICE_ADDRESS_REQUIREMENT_KEY = 'organization.practice_address';

/** Names the upload an entry came from, so two licences are told apart by document. */
function RecordDocumentChip({ documentName }: { documentName?: string }) {
  if (!documentName) return null;
  return (
    <Box
      sx={{
        color: wf.muted,
        fontSize: '11px',
        marginBottom: '8px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      From {documentName}
    </Box>
  );
}

interface RepeatableIntakeGroupProps {
  fields: GeneratedIntakeField[];
  records: IntakeRepeatRecord[];
  acknowledgedRecords?: IntakeRepeatRecord[];
  onDraftChange?: (records: IntakeRepeatRecord[]) => void;
  suggestions?: Record<string, IntakeFieldSuggestion>;
  /** Suggestion entry key to the document reference that produced it. */
  suggestionDocumentReferenceIds?: Record<string, string>;
  /** Document reference id to the name shown for that document. */
  documentNameByReferenceId?: Record<string, string>;
  onPersist: (records: IntakeRepeatRecord[]) => Promise<void>;
  onAcceptSuggestion?: (suggestion: IntakeFieldSuggestion) => Promise<void>;
  onDiscardSuggestion?: (suggestion: IntakeFieldSuggestion) => void;
  showRequiredErrors?: boolean;
  saveStateKeyPrefix?: string;
  onSaveStateChange?: (key: string, state: IntakeFieldSaveState) => void;
}

export function RepeatableIntakeGroup({
  fields,
  records,
  acknowledgedRecords: savedRecords,
  onDraftChange,
  suggestions = {},
  suggestionDocumentReferenceIds = {},
  documentNameByReferenceId = {},
  onPersist,
  onAcceptSuggestion,
  onDiscardSuggestion,
  showRequiredErrors = false,
  saveStateKeyPrefix = '',
  onSaveStateChange,
}: RepeatableIntakeGroupProps) {
  const [localDraftRecords, setDraftRecords] = useState(records);
  const [localAcknowledgedRecords, setAcknowledgedRecords] = useState(records);
  const draftRecords = onDraftChange ? records : localDraftRecords;
  const acknowledgedRecords = savedRecords ?? localAcknowledgedRecords;
  const updateDraft = (next: IntakeRepeatRecord[]) => {
    setDraftRecords(next);
    onDraftChange?.(next);
  };
  const [saveError, setSaveError] = useState<string>();
  const handleSaveStateChange = useCallback(
    (_field: GeneratedIntakeField, state: IntakeFieldSaveState, saveStateKey?: string) => {
      if (saveStateKey) onSaveStateChange?.(saveStateKey, state);
    },
    [onSaveStateChange],
  );
  const signature = JSON.stringify(records);
  useEffect(() => {
    setDraftRecords(records);
    if (savedRecords === undefined) setAcknowledgedRecords(records);
  }, [signature, savedRecords]); // eslint-disable-line react-hooks/exhaustive-deps

  const duplicatePolicy = repeatDuplicatePolicy(fields);
  const isPracticeAddress = fields[0]?.requirement_key === PRACTICE_ADDRESS_REQUIREMENT_KEY;
  const duplicateError = duplicatePolicy
    ? duplicateRepeatRecordError(draftRecords, duplicatePolicy.fieldKeys, duplicatePolicy.message)
    : undefined;
  /**
   * Suggestions placed on the record they target, keyed record id → field id.
   * A repeated field takes one suggestion per record, so this is a map of maps
   * rather than the single per-field entry a non-repeated page uses.
   */
  const suggestionsByRecord = useMemo(() => {
    const fieldsById = new Map(fields.map(field => [field.field_id, field]));
    const byRecord = new Map<string, Map<string, IntakeFieldSuggestion>>();
    for (const suggestion of Object.values(suggestions)) {
      const field = fieldsById.get(suggestion.field_id);
      if (!field) continue;
      const recordId = repeatRecordIdForSuggestion(
        draftRecords,
        field.destination.field_key,
        suggestion,
        isPracticeAddress,
      );
      if (!recordId) continue;
      const forRecord = byRecord.get(recordId) ?? new Map<string, IntakeFieldSuggestion>();
      if (!forRecord.has(suggestion.field_id)) forRecord.set(suggestion.field_id, suggestion);
      byRecord.set(recordId, forRecord);
    }
    return byRecord;
  }, [draftRecords, fields, isPracticeAddress, suggestions]);

  /**
   * Records named by suggestions that the server has not created yet — it opens
   * a record lazily, on the first accepted suggestion for a document. They are
   * shown so the second uploaded licence is visible and acceptable, but they
   * are deliberately kept out of draft state: creating them is the server's
   * job, and a locally-added record would be persisted without the document
   * link the server owns.
   */
  const pendingRecordIds = useMemo(
    () => pendingSuggestionRecordIds(draftRecords, [...suggestionsByRecord.keys()]),
    [draftRecords, suggestionsByRecord],
  );
  const displayedRecords = draftRecords.filter(
    record =>
      !(
        pendingRecordIds.length > 0 &&
        record.isPlaceholder &&
        !Object.values(record.values).some(value => hasDurableValue(value))
      ),
  );

  const documentNameForRecord = useCallback(
    (recordId: string, record?: IntakeRepeatRecord): string | undefined => {
      const linked = record?.document_reference_id;
      if (linked) return documentNameByReferenceId[linked];
      // A record with no stored link (a pending one) is identified by the
      // document that produced its suggestions.
      for (const fieldId of suggestionsByRecord.get(recordId)?.keys() ?? []) {
        const referenceId = suggestionDocumentReferenceIds[suggestionEntryKey(fieldId, recordId)];
        const name = referenceId ? documentNameByReferenceId[referenceId] : undefined;
        if (name) return name;
      }
      return undefined;
    },
    [documentNameByReferenceId, suggestionDocumentReferenceIds, suggestionsByRecord],
  );

  const persist = async (next: IntakeRepeatRecord[]) => {
    if (
      duplicatePolicy &&
      duplicateRepeatRecordError(next, duplicatePolicy.fieldKeys, duplicatePolicy.message)
    ) {
      throw new Error('Duplicate repeated state');
    }
    updateDraft(next);
    setSaveError(undefined);
    await onPersist(next);
    if (savedRecords === undefined) setAcknowledgedRecords(next);
  };

  const persistAction = (next: IntakeRepeatRecord[]) => {
    void persist(next).catch(() => {
      setSaveError('This entry could not be saved. Try again.');
    });
  };

  const addRecord = () => {
    const next = addRepeatRecord(displayedRecords, crypto.randomUUID());
    persistAction(next);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {duplicateError && (
        <Box role="alert" sx={{ color: 'error.main', fontSize: '0.8rem' }}>
          {duplicateError}
        </Box>
      )}
      {saveError && (
        <Box role="alert" sx={{ color: 'error.main', fontSize: '0.8rem' }}>
          {saveError}
        </Box>
      )}
      {displayedRecords.map((record, recordIndex) => {
        const acknowledged = acknowledgedRecords.find(
          candidate => candidate.record_id === record.record_id,
        );
        const recordLabel = isPracticeAddress
          ? recordIndex === 0
            ? 'Primary practice address'
            : `Practice location ${recordIndex + 1}`
          : `${fields[0]?.group_name ?? 'Entry'} ${recordIndex + 1}`;
        return (
          <Box
            key={record.record_id}
            component="fieldset"
            sx={{ ...cardSx, margin: 0, minWidth: 0, padding: '14px' }}
          >
            <Box
              component="legend"
              sx={{ color: wf.ink, fontSize: '13px', fontWeight: 700, padding: '0 6px' }}
            >
              {recordLabel}
            </Box>
            <RecordDocumentChip documentName={documentNameForRecord(record.record_id, record)} />
            {recordIndex > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  aria-label={
                    isPracticeAddress
                      ? `Remove practice location ${recordIndex + 1}`
                      : `Remove ${fields[0]?.group_name ?? 'entry'} ${recordIndex + 1}`
                  }
                  onClick={() => persistAction(removeRepeatRecord(draftRecords, record.record_id))}
                  size="small"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '10px',
              }}
            >
              {fields.map(field => {
                const fieldKey = field.destination.field_key;
                const draftValue = record.values[fieldKey] ?? defaultFieldValue(field);
                const acknowledgedValue =
                  acknowledged?.values[fieldKey] ?? defaultFieldValue(field);
                const suggestion = suggestionsByRecord.get(record.record_id)?.get(field.field_id);
                return (
                  <IntakePageFieldInput
                    key={`${record.record_id}:${field.field_id}`}
                    field={field}
                    draftValue={draftValue}
                    acknowledgedValue={acknowledgedValue}
                    hasAcknowledgedValue={hasDurableValue(acknowledgedValue)}
                    onDraftChange={(_, value) =>
                      updateDraft(
                        updateRepeatRecord(draftRecords, record.record_id, fieldKey, value),
                      )
                    }
                    onPersist={async (_, value) =>
                      persist(updateRepeatRecord(draftRecords, record.record_id, fieldKey, value))
                    }
                    suggestion={suggestion}
                    onAcceptSuggestion={
                      suggestion && onAcceptSuggestion
                        ? accepted => onAcceptSuggestion(accepted)
                        : undefined
                    }
                    onDiscardSuggestion={onDiscardSuggestion}
                    showStatus={false}
                    saveStateKey={`${saveStateKeyPrefix}${record.record_id}:${field.field_id}`}
                    onSaveStateChange={handleSaveStateChange}
                    validationError={
                      showRequiredErrors && field.is_required && !hasDurableValue(draftValue)
                        ? `${field.label} is required.`
                        : undefined
                    }
                  />
                );
              })}
            </Box>
          </Box>
        );
      })}
      {pendingRecordIds.map((recordId, pendingIndex) => {
        const recordSuggestions = suggestionsByRecord.get(recordId);
        const suggestedFields = fields.filter(field => recordSuggestions?.has(field.field_id));
        if (suggestedFields.length === 0) return null;
        const entryName = fields[0]?.group_name ?? 'Entry';
        return (
          <Box
            key={recordId}
            component="fieldset"
            aria-label={`${entryName} ${displayedRecords.length + pendingIndex + 1}, suggested from a document`}
            sx={{ ...cardSx, margin: 0, minWidth: 0, padding: '14px' }}
          >
            <Box
              component="legend"
              sx={{ color: wf.ink, fontSize: '13px', fontWeight: 700, padding: '0 6px' }}
            >
              {`${entryName} ${displayedRecords.length + pendingIndex + 1}`}
            </Box>
            <RecordDocumentChip documentName={documentNameForRecord(recordId)} />
            <Box sx={{ color: wf.muted, fontSize: '12px', mb: '8px' }}>
              Accept the suggested values to create this entry.
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '10px',
              }}
            >
              {suggestedFields.map(field => {
                const suggestion = recordSuggestions?.get(field.field_id);
                return (
                  <IntakePageFieldInput
                    key={`${recordId}:${field.field_id}`}
                    field={field}
                    draftValue={defaultFieldValue(field)}
                    acknowledgedValue={defaultFieldValue(field)}
                    hasAcknowledgedValue={false}
                    onDraftChange={() => undefined}
                    onPersist={() => Promise.resolve()}
                    readOnly
                    suggestion={suggestion}
                    onAcceptSuggestion={
                      suggestion && onAcceptSuggestion
                        ? accepted => onAcceptSuggestion(accepted)
                        : undefined
                    }
                    onDiscardSuggestion={onDiscardSuggestion}
                    showStatus={false}
                  />
                );
              })}
            </Box>
          </Box>
        );
      })}
      <Button variant="outlined" onClick={addRecord} sx={{ alignSelf: 'flex-start' }}>
        Add another {fields[0]?.group_name?.toLowerCase() ?? 'entry'}
      </Button>
    </Box>
  );
}
